import * as React from 'react'

import { Modal, Space, SubText, TransparentIconButton } from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import {
  GlobalSearchModalLayoutStyled,
  GlobalSearchModalHeaderAreaStyled,
  GlobalSearchModalContentAreaStyled,
  GlobalSearchModalCloseAreaStyled,
} from './styles/GlobalSearchModalLayoutStyled'
import { type GlobalSearchRecentItem } from '../../../../value-objects/GlobalSearchRecentItem'
import { type GlobalSearchResultItem } from '../../../../value-objects/GlobalSearchResultItem'
import {
  ContentStates,
  type ContentStateEnum,
} from '../../enums/ContentStateEnum'
import {
  type GlobalSearchTab,
  type GlobalSearchTabTypeEnum,
} from '../../enums/GlobalSearchTabTypeEnum'
import { GlobalSearchEmptyState } from '../GlobalSearchEmptyState'
import { GlobalSearchErrorState } from '../GlobalSearchErrorState'
import { GlobalSearchInput } from '../GlobalSearchInput'
import { GlobalSearchLoadingState } from '../GlobalSearchLoadingState'
import { GlobalSearchRecentsList } from '../GlobalSearchRecentsList'
import { GlobalSearchResultsList } from '../GlobalSearchResultsList'
import { GlobalSearchResultTabs } from '../GlobalSearchResultTabs'
import { GlobalSearchUnsupportedQueryState } from '../GlobalSearchUnsupportedQueryState'

/**
 * Dismiss open popovers when the modal opens. Triggers ClickAwayListener
 * on Tooltip-based popovers. Fires during onEnter, after MUI captures the
 * previously focused element for restoration.
 */
const dismissPopovers = () => document.body.click()

const LISTBOX_ID = 'global-search-listbox'

interface IGlobalSearchModalProps {
  isOpen: boolean
  query: string
  showEnterHint: boolean
  contentState: Nullable<ContentStateEnum>
  recentItems: GlobalSearchRecentItem[]
  resultItems: GlobalSearchResultItem[]
  tabs: Nullable<GlobalSearchTab[]>
  selectedTab: GlobalSearchTabTypeEnum
  showTruncatedNotice: boolean
  inputRef: React.RefObject<HTMLInputElement>
  highlightedIndex: Nullable<number>
  activeDescendantId: string | undefined
  isExpanded: boolean
  onClose: () => void
  onQueryChange: (value: string) => void
  onEnter: () => void
  onNavigateDown: () => void
  onNavigateUp: () => void
  onRecentItemClick: (item: GlobalSearchRecentItem) => void
  onResultItemClick: (item: GlobalSearchResultItem) => void
  onTabChange: (type: GlobalSearchTabTypeEnum) => void
}

export const GlobalSearchModal = ({
  isOpen,
  query,
  showEnterHint,
  contentState,
  recentItems,
  resultItems,
  tabs,
  selectedTab,
  showTruncatedNotice,
  inputRef,
  highlightedIndex,
  activeDescendantId,
  isExpanded,
  onClose,
  onQueryChange,
  onEnter,
  onNavigateDown,
  onNavigateUp,
  onRecentItemClick,
  onResultItemClick,
  onTabChange,
}: IGlobalSearchModalProps) => {
  const intl = useProcurifyIntl()

  const focusInput = () => {
    inputRef.current?.focus()
  }

  return (
    <Modal
      aria-label={intl.formatMessage(messages.modalAriaLabel)}
      maxWidth='sm'
      fullWidth
      open={isOpen}
      onClose={onClose}
      keepMounted
      TransitionProps={{
        onEnter: dismissPopovers,
        onEntered: focusInput,
      }}
    >
      <Modal.Card cycleFocus>
        <GlobalSearchModalLayoutStyled>
          <GlobalSearchModalHeaderAreaStyled>
            <Modal.Content pt={0} pr={0} pb={0}>
              <Space direction='horizontal' size='small' align='center'>
                <Space.Item grow={1} flex>
                  <GlobalSearchInput
                    listboxId={LISTBOX_ID}
                    query={query}
                    inputRef={inputRef}
                    activeDescendantId={activeDescendantId}
                    isExpanded={isExpanded}
                    onQueryChange={onQueryChange}
                    onEnter={onEnter}
                    onNavigateDown={onNavigateDown}
                    onNavigateUp={onNavigateUp}
                  />
                </Space.Item>
                {showEnterHint && (
                  <Space.Item flex>
                    <SubText inline noWrap color='textTertiary'>
                      {intl.formatMessage(messages.enterToSearchHint)}
                    </SubText>
                  </Space.Item>
                )}
              </Space>
            </Modal.Content>
          </GlobalSearchModalHeaderAreaStyled>

          <GlobalSearchModalContentAreaStyled>
            {contentState === ContentStates.RECENTS && (
              <Modal.Content pl={0.5} pt={0} pr={0.5} pb={1}>
                <GlobalSearchRecentsList
                  listboxId={LISTBOX_ID}
                  items={recentItems}
                  highlightedIndex={highlightedIndex}
                  onRecentItemClick={onRecentItemClick}
                />
              </Modal.Content>
            )}
            {contentState === ContentStates.LOADING && (
              <Modal.Content pl={0.5} pt={0} pr={0.5} pb={1}>
                <GlobalSearchLoadingState />
              </Modal.Content>
            )}
            {contentState === ContentStates.RESULTS && (
              <>
                {tabs && (
                  <Modal.Content pl={0.5} pt={0} pr={0.5} pb={0.5}>
                    <GlobalSearchResultTabs
                      tabs={tabs}
                      selectedTab={selectedTab}
                      onTabChange={onTabChange}
                    />
                  </Modal.Content>
                )}
                <Modal.Content pl={0.5} pt={0} pr={0.5} pb={1}>
                  <GlobalSearchResultsList
                    key={selectedTab}
                    listboxId={LISTBOX_ID}
                    items={resultItems}
                    showTruncatedNotice={showTruncatedNotice}
                    highlightedIndex={highlightedIndex}
                    onResultItemClick={onResultItemClick}
                  />
                </Modal.Content>
              </>
            )}
            {contentState === ContentStates.EMPTY && (
              <Modal.Content pl={0.5} pt={0} pr={0.5} pb={1}>
                <GlobalSearchEmptyState />
              </Modal.Content>
            )}
            {contentState === ContentStates.UNSUPPORTED_QUERY && (
              <Modal.Content pl={0.5} pt={0} pr={0.5} pb={1}>
                <GlobalSearchUnsupportedQueryState />
              </Modal.Content>
            )}
            {contentState === ContentStates.ERROR && (
              <Modal.Content pl={0.5} pt={0} pr={0.5} pb={1}>
                <GlobalSearchErrorState />
              </Modal.Content>
            )}
          </GlobalSearchModalContentAreaStyled>

          <GlobalSearchModalCloseAreaStyled>
            <TransparentIconButton
              aria-label={intl.formatMessage(messages.closeButtonAriaLabel)}
              icon='times'
              onClick={onClose}
            />
          </GlobalSearchModalCloseAreaStyled>
        </GlobalSearchModalLayoutStyled>
      </Modal.Card>
    </Modal>
  )
}
